import { ServerStatus, type ServerSummary } from '@bartizo/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { listServerGroups, listServers, type ServerFilters } from '../api/servers.js';
import { useCurrentUser } from '../auth/session-context.js';
import { EmptyState } from '../components/EmptyState.js';
import { DataTable, type Column } from '../ui/DataTable.js';
import { PageHeader } from '../components/PageHeader.js';
import { ServerStatusBadge } from '../components/StatusBadge.js';
import { Facts } from '../ui/DataTable.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { getOverview } from '../api/overview.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { CheckIcon, HandIcon, ServerIcon, ServerOffIcon } from '../ui/icons.js';
import { StatIcon } from '../ui/StatIcon.js';
import { SelectField, TextField } from '../ui/Field.js';

/**
 * The fleet (knowledge doc section 15: filter by server, group and status,
 * paginated).
 *
 * Paging is forward-only with a stack of the cursors already visited, which is
 * what a cursor gives and an offset does not: no page is ever skipped or repeated
 * because a server enrolled while somebody was reading.
 */

const ALL = '__all__';

export function ServersPage(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);

  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>(ALL);
  // The Groups page links here with its group chosen.
  const [params] = useSearchParams();
  const [groupId, setGroupId] = useState<string>(params.get('group_id') ?? ALL);
  /** The cursor for the page being shown, and the ones behind it. */
  const [history, setHistory] = useState<(string | undefined)[]>([undefined]);
  const cursor = history[history.length - 1];

  const filters: ServerFilters = {
    ...(search.trim().length > 0 ? { q: search.trim() } : {}),
    ...(status === ALL ? {} : { status: ServerStatus.parse(status) }),
    ...(groupId === ALL ? {} : { group_id: groupId }),
    ...(cursor === undefined ? {} : { cursor }),
  };

  const servers = useQuery({
    queryKey: ['servers', filters],
    queryFn: () => listServers(filters),
    // Keeps the table on screen while the next page loads, rather than blanking.
    placeholderData: keepPreviousData,
  });

  const fleet = useQuery({ queryKey: ['overview'], queryFn: getOverview }).data?.fleet;
  const groups = useQuery({ queryKey: ['server-groups'], queryFn: listServerGroups });

  /** Any filter change starts the paging over; the old cursors are for other results. */
  const resetPaging = (): void => {
    setHistory([undefined]);
  };

  return (
    <div className="page">
      <PageHeader
        title="Servers"
        description="Every machine that has enrolled, and what it is doing."
        actions={
          session.user.role === 'admin' ? (
            <Link to="/enrollment-tokens" className="button button-primary">
              Add a server
            </Link>
          ) : undefined
        }
      />

      {fleet !== undefined && (
        <dl className="stat-tiles">
          <div className="stat-tile">
            <StatIcon icon={ServerIcon} />
            <dt>Total servers</dt>
            <dd>{String(fleet.active + fleet.offline + fleet.pending)}</dd>
          </div>
          <div className="stat-tile">
            <StatIcon icon={CheckIcon} tone="ok" />
            <dt>Online</dt>
            <dd>{String(fleet.active)}</dd>
          </div>
          <div className="stat-tile">
            <StatIcon icon={HandIcon} tone={fleet.pending > 0 ? 'danger' : 'muted'} />
            <dt>Waiting for approval</dt>
            <dd>{String(fleet.pending)}</dd>
          </div>
          <div className={`stat-tile ${fleet.offline > 0 ? 'stat-tile-alert' : ''}`}>
            <StatIcon icon={ServerOffIcon} tone={fleet.offline > 0 ? 'danger' : 'muted'} />
            <dt>Offline</dt>
            <dd>{String(fleet.offline)}</dd>
          </div>
        </dl>
      )}

      <div className="filters">
        <TextField
          label="Search"
          hint="Matches the start of a name or hostname."
          type="search"
          placeholder="prod-web"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            resetPaging();
          }}
        />
        <SelectField
          label="Status"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            resetPaging();
          }}
        >
          <option value={ALL}>Any status</option>
          {ServerStatus.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Group"
          value={groupId}
          onChange={(event) => {
            setGroupId(event.target.value);
            resetPaging();
          }}
        >
          <option value={ALL}>Any group</option>
          {(groups.data ?? []).map((group) => (
            <option key={group.id} value={group.id}>
              {group.name}
            </option>
          ))}
        </SelectField>
        <Button
          onClick={() => {
            setSearch('');
            setStatus(ALL);
            setGroupId(ALL);
            resetPaging();
          }}
        >
          Reset
        </Button>
      </div>

      {servers.isError && (
        <Alert tone="danger" title="Could not load the fleet">
          {servers.error instanceof Error ? servers.error.message : 'Try again in a moment.'}
        </Alert>
      )}

      {servers.isPending && (
        <p className="row muted" aria-busy="true">
          <span className="spinner" aria-hidden="true" />
          Loading servers…
        </p>
      )}

      {servers.data?.items.length === 0 && (
        <EmptyState title="No servers match">
          {search.length > 0 || status !== ALL || groupId !== ALL
            ? 'Try widening the filters.'
            : 'Create an enrollment token and run the installer on a server to add the first one.'}
        </EmptyState>
      )}

      {servers.data !== undefined && servers.data.items.length > 0 && (
        <>
          <DataTable
            caption="Servers, with their status, what needs a look, group and last heartbeat"
            columns={serverColumns(timezone)}
            rows={servers.data.items}
            rowKey={(server) => server.id}
            onSelect={(server) => {
              void navigate(`/servers/${encodeURIComponent(server.id)}`);
            }}
            initialSort={{ key: 'attention', direction: 'desc' }}
            renderDetail={(server) => <ServerDetail server={server} timezone={timezone} />}
          />

          <div className="row">
            <Button
              disabled={history.length === 1}
              onClick={() => {
                setHistory((current) => current.slice(0, -1));
              }}
            >
              Previous
            </Button>
            <Button
              disabled={servers.data.nextCursor === null}
              onClick={() => {
                const next = servers.data.nextCursor;
                if (next !== null) setHistory((current) => [...current, next]);
              }}
            >
              Next
            </Button>
            <span className="muted text-sm">
              Page {history.length}
              {servers.isFetching ? ' · loading…' : ''}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/** How much needs a person on a server, for sorting the worst first. */
const weight = (server: ServerSummary): number => {
  const attention = server.attention;
  const offline = server.status === 'offline' ? 100 : 0;
  if (attention === undefined) return offline;
  return (
    offline +
    attention.openHigh * 10 +
    attention.unhealthy.length * 5 +
    (attention.settingsFailed ? 20 : 0) +
    (attention.wazuhAgent === 'disconnected' ? 30 : attention.wazuhAgent === null ? 0 : 10)
  );
};

/** What needs a look, in words, worst first; nothing when nothing does. */
function Attention({ server }: { server: ServerSummary }): ReactNode {
  const attention = server.attention;
  const items: { tone: string; text: string }[] = [];
  if (server.status === 'offline') items.push({ tone: 'badge-danger', text: 'Offline' });
  if (attention !== undefined) {
    if (attention.openHigh > 0) {
      items.push({
        tone: 'badge-danger',
        text: `${String(attention.openHigh)} high event${attention.openHigh === 1 ? '' : 's'} open`,
      });
    }
    if (attention.wazuhAgent === 'disconnected') {
      items.push({ tone: 'badge-danger', text: 'Wazuh agent disconnected' });
    } else if (attention.wazuhAgent === 'never_connected') {
      items.push({ tone: 'badge-warning', text: 'Wazuh agent never connected' });
    } else if (attention.wazuhAgent === 'not_enrolled') {
      items.push({ tone: 'badge-warning', text: 'Wazuh agent not enrolled' });
    }
    if (attention.settingsFailed) items.push({ tone: 'badge-warning', text: 'Settings failed' });
    for (const module of attention.unhealthy.slice(0, 2)) {
      items.push({ tone: 'badge-warning', text: `${module.replace(/_/g, ' ')} unhealthy` });
    }
    if (attention.unhealthy.length > 2) {
      items.push({
        tone: 'badge-warning',
        text: `+${String(attention.unhealthy.length - 2)} more`,
      });
    }
  }
  if (items.length === 0) {
    return server.status === 'active' ? <span className="muted text-sm">All fine</span> : null;
  }
  return (
    <span className="badge-list">
      {items.map((item) => (
        <span key={item.text} className={`badge ${item.tone}`}>
          {item.text}
        </span>
      ))}
    </span>
  );
}

function serverColumns(timezone: string): Column<ServerSummary>[] {
  return [
    {
      key: 'name',
      header: 'Server',
      rowHeader: true,
      sortValue: (server) => server.name,
      cell: (server) => (
        <>
          <Link to={`/servers/${encodeURIComponent(server.id)}`}>{server.name}</Link>
          <span className="cell-sub">
            {server.hostname}
            {server.os !== null && ` · ${server.os}`}
          </span>
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (server) => server.status,
      cell: (server) => (
        <>
          <ServerStatusBadge status={server.status} />
          <span className="cell-sub">
            {server.agentVersion === null ? 'agent not reporting' : `agent ${server.agentVersion}`}
          </span>
        </>
      ),
    },
    {
      key: 'attention',
      header: 'Needs a look',
      sortValue: weight,
      cell: (server) => <Attention server={server} />,
    },
    {
      key: 'roles',
      header: 'Roles',
      wideOnly: true,
      cell: (server) => (
        <span className="badge-list">
          {server.roles.map((role) => (
            <span key={role} className="badge badge-muted">
              {role}
            </span>
          ))}
        </span>
      ),
    },
    {
      key: 'group',
      header: 'Group',
      wideOnly: true,
      sortValue: (server) => server.group?.name ?? '',
      cell: (server) => server.group?.name ?? <span className="muted">—</span>,
    },
    {
      key: 'view',
      header: '',
      cell: (server) => (
        <Link to={`/events?server_id=${encodeURIComponent(server.id)}`}>View events</Link>
      ),
    },
    {
      key: 'heartbeat',
      header: 'Last heartbeat',
      sortValue: (server) => (server.lastHeartbeat === null ? 0 : Date.parse(server.lastHeartbeat)),
      cell: (server) => (
        <span title={timezone}>
          {server.lastHeartbeat === null ? (
            <span className="muted">never</span>
          ) : (
            formatRelative(server.lastHeartbeat)
          )}
          {server.ip !== null && <span className="cell-sub">{server.ip}</span>}
        </span>
      ),
    },
  ];
}

/** What a person wants next to a server's row: who it is, what it runs, what is wrong. */
function ServerDetail({
  server,
  timezone,
}: {
  server: ServerSummary;
  timezone: string;
}): ReactNode {
  const id = encodeURIComponent(server.id);
  const labels = Object.entries(server.labels);
  const attention = server.attention;
  return (
    <div className="detail-grid">
      <section>
        <h4>Machine</h4>
        <Facts
          items={[
            ['Hostname', server.hostname],
            ['Address (self-reported)', server.ip ?? '—'],
            ['System', server.os ?? '—'],
            ['Agent', server.agentVersion ?? '—'],
            ['Enrolled', formatInstant(server.createdAt, { timezone })],
          ]}
        />
      </section>
      <section>
        <h4>Role and group</h4>
        <Facts
          items={[
            ['Roles', server.roles.length === 0 ? '—' : server.roles.join(', ')],
            [
              'Group',
              server.group === null ? (
                '—'
              ) : (
                <Link to={`/servers?group_id=${encodeURIComponent(server.group.id)}`}>
                  {server.group.name}
                </Link>
              ),
            ],
            [
              'Labels',
              labels.length === 0 ? (
                '—'
              ) : (
                <span className="badge-list">
                  {labels.map(([key, value]) => (
                    <span key={key} className="badge badge-muted">
                      {key}: {value}
                    </span>
                  ))}
                </span>
              ),
            ],
          ]}
        />
      </section>
      <section>
        <h4>Health</h4>
        <Facts
          items={[
            [
              'Open high events',
              attention === undefined ? (
                '—'
              ) : (
                <Link to={`/events?server_id=${id}&min_severity=high`}>
                  {String(attention.openHigh)}
                </Link>
              ),
            ],
            [
              'Unhealthy modules',
              attention === undefined || attention.unhealthy.length === 0
                ? 'None'
                : attention.unhealthy.map((module) => module.replace(/_/g, ' ')).join(', '),
            ],
            ['Latest settings', attention?.settingsFailed === true ? 'Failed to apply' : 'Applied'],
            [
              'Wazuh agent',
              attention?.wazuhAgent == null
                ? 'Fine or not used'
                : attention.wazuhAgent.replace(/_/g, ' '),
            ],
          ]}
        />
      </section>
      <section>
        <h4>Go to</h4>
        <span className="link-list">
          <Link to={`/servers/${id}`}>Server page</Link>
          <Link to={`/servers/${id}?tab=events`}>Events</Link>
          <Link to={`/servers/${id}?tab=configuration`}>Configuration</Link>
        </span>
      </section>
    </div>
  );
}
